if (!customElements.get('product-duration-calculator')) {
  class ProductDurationCalculator extends HTMLElement {
    constructor() {
      super();
      this.weightInput = this.querySelector('.product-duration-calculator__weight-input');
      this.conditionInput = this.querySelector('.product-duration-calculator__condition-input');
      this.submitButton = this.querySelector('.product-duration-calculator__submit');
      this.resultDisplay = this.querySelector('.product-duration-calculator__result');

      const dataScript = this.querySelector('.product-duration-calculator__data');
      const rawData = dataScript ? JSON.parse(dataScript.textContent) : { feedingGuide: [], durationGuideByVariant: {} };
      this.data = {
        feedingGuide: (rawData.feedingGuide || []).map(ProductDurationCalculator.normalizeRow),
        durationGuideByVariant: rawData.durationGuideByVariant || {},
      };
      this.selectedVariantId = this.dataset.selectedVariantId;

      this.submitButton.addEventListener('click', this.calculate.bind(this));

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.dataset.sectionId) return;
          this.selectedVariantId = event.data.variant.id.toString();
          this.resultDisplay.hidden = true;
        });
      }
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    }

    /**
     * Public update hook for product-nutrition-locale-fallback.js — replaces
     * this.data in place once real data is fetched via the Storefront API,
     * without touching the DOM/listeners set up in the constructor.
     * @param {Array} feedingGuide - raw feedingGuide rows (weight/dose strings)
     * @param {Object} durationGuideByVariant
     */
    updateData(feedingGuide, durationGuideByVariant) {
      this.data = {
        feedingGuide: (feedingGuide || []).map(ProductDurationCalculator.normalizeRow),
        durationGuideByVariant: durationGuideByVariant || {},
      };
    }

    /**
     * Extracts the leading number from a unit-suffixed string (e.g. "25 g" -> 25,
     * "1.5 kg" -> 1.5). catalog.feeding_guide/duration_guide rows carry
     * self-describing strings ("weight"/"dose"/"duration") instead of bare
     * numbers — see formatKg/formatGrams in Backend's pushCatalogToShopify.ts.
     * @param {string|number|null|undefined} value
     * @returns {number|null}
     */
    static parseNumber(value) {
      if (value == null) return null;
      if (typeof value === 'number') return value;
      const match = String(value).match(/-?\d+(\.\d+)?/);
      return match ? parseFloat(match[0]) : null;
    }

    /**
     * Normalizes a feedingGuide/durationGuide row to plain numeric fields so
     * calculation logic never has to parse strings itself.
     * @param {object} row
     */
    static normalizeRow(row) {
      return {
        label: row.label,
        conditions: row.conditions,
        weightKg: ProductDurationCalculator.parseNumber(row.weight),
        gramsPerDay: ProductDurationCalculator.parseNumber(row.dose),
      };
    }

    /**
     * Linearly interpolates gramsPerDay for an arbitrary animal weight between
     * the two nearest tabulated rows (for the selected condition), clamping to
     * the table's edges when the weight falls outside its range.
     * @param {Array} rows - normalized feedingGuide rows already filtered to one condition, sorted by weightKg.
     * @param {number} animalWeightKg
     * @returns {number|null}
     */
    static interpolateGramsPerDay(rows, animalWeightKg) {
      const validRows = rows.filter((row) => row.weightKg != null && row.gramsPerDay != null);
      if (!validRows.length) return null;

      if (animalWeightKg <= validRows[0].weightKg) return validRows[0].gramsPerDay;
      if (animalWeightKg >= validRows[validRows.length - 1].weightKg) return validRows[validRows.length - 1].gramsPerDay;

      for (let i = 0; i < validRows.length - 1; i += 1) {
        const lower = validRows[i];
        const upper = validRows[i + 1];

        if (animalWeightKg >= lower.weightKg && animalWeightKg <= upper.weightKg) {
          const ratio = (animalWeightKg - lower.weightKg) / (upper.weightKg - lower.weightKg);
          return lower.gramsPerDay + ratio * (upper.gramsPerDay - lower.gramsPerDay);
        }
      }

      return null;
    }

    /**
     * Resolves the translated animal noun (dog/cat/generic) from the
     * data-animal-type attribute set by product-duration-calculator.liquid.
     * @returns {string}
     */
    getAnimalNoun() {
      const strings = window.strings.product.durationCalculator;
      if (this.dataset.animalType === 'dog') return strings.animalDog;
      if (this.dataset.animalType === 'cat') return strings.animalCat;
      return strings.animalGeneric;
    }

    calculate() {
      const animalWeightKg = parseFloat(this.weightInput.value);

      if (!animalWeightKg || animalWeightKg <= 0) {
        this.showResult(window.strings.product.durationCalculator.invalidWeight, true);
        return;
      }

      const variantData = this.data.durationGuideByVariant[this.selectedVariantId];

      if (!variantData || !variantData.packWeightKg) {
        this.showResult(window.strings.product.durationCalculator.unavailable, true);
        return;
      }

      const selectedCondition = this.conditionInput.value;
      const rows = this.data.feedingGuide
        .filter((row) => !selectedCondition || row.conditions === selectedCondition)
        .slice()
        .sort((a, b) => a.weightKg - b.weightKg);

      const gramsPerDay = ProductDurationCalculator.interpolateGramsPerDay(rows, animalWeightKg);

      if (!gramsPerDay) {
        this.showResult(window.strings.product.durationCalculator.unavailable, true);
        return;
      }

      const weeks = (variantData.packWeightKg * 1000) / (gramsPerDay * 7);
      const roundedWeeks = Math.round(weeks * 10) / 10;
      const roundedGramsPerDay = Math.round(gramsPerDay);

      const message = window.strings.product.durationCalculator.result
        .replace('[weeks]', roundedWeeks)
        .replace('[weight]', animalWeightKg)
        .replace('[animal]', this.getAnimalNoun())
        .replace('[dose]', roundedGramsPerDay);

      this.showResult(message, false);
    }

    showResult(message, isError) {
      this.resultDisplay.innerHTML = message;
      this.resultDisplay.classList.toggle('product-duration-calculator__result--error', isError);
      this.resultDisplay.hidden = false;
    }
  }

  customElements.define('product-duration-calculator', ProductDurationCalculator);
}
